import React, { useEffect, useRef, useState } from 'react';
import {
  enterGame,
  EnterGameInput,
  EnterGameResponse,
} from '../../services/enterToGame.service';
import { exitGame, ExitGameInput } from '../../services/exitGame.service';
import { authService } from '../../services/auth.service';
import { Loading } from '../Loading';
import styles from './EnterGame.module.css';
import GameLoadingSplash from '../GameLoadingSplash/GameLoadingSplash';
import { useGameLoadState } from '../GameLoadingSplash/useGameLoadState';
import { getErrorMessage } from '../../utils/helpers/getErrorMessage';
import Modal from '../User/Modal';
import { useLocation, useNavigate } from 'react-router-dom';
import { updateGamePosition } from '../../services/updateGamePosition.service';
import { addGameAssignment } from '../../services/addGameAssignment.service';
import { removeGameAssignment } from '../../services/removeGameAssignment.service';

interface EnterGameModalProps {
  isOpen: boolean;
  onClose: () => void;
  id: number; // numeric id for position updates
  gameId: string; // string id for launching game
  userId: number;
  providerId: number;
  gameName?: string;
  position?: number;
  isActive?: boolean;
  environmentId: number;
  labelId: number;
}

export const EnterGameModal: React.FC<EnterGameModalProps> = ({
  isOpen,
  onClose,
  id,
  gameId,
  userId,
  providerId,
  gameName,
  position,
  isActive,
  environmentId,
  labelId,
}) => {
  const [gameData, setGameData] = useState<EnterGameResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Editable position state
  const [editingPosition, setEditingPosition] = useState(false);
  const [positionInput, setPositionInput] = useState(
    position ? String(position) : '',
  );
  const [positionUpdating, setPositionUpdating] = useState(false);
  const [positionSuccess, setPositionSuccess] = useState(false);
  // Active status state
  const [updatingActive, setUpdatingActive] = useState(false);
  const [localIsActive, setLocalIsActive] = useState(isActive);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const { state: loadState, onIframeLoad } = useGameLoadState(
    iframeRef,
    gameData?.launchUrl,
    String(gameId),
  );

  useEffect(() => {
    setPositionInput(position ? String(position) : '');
    setLocalIsActive(isActive);
  }, [position, isActive]);

  useEffect(() => {
    if (!isOpen) return;
    // Check if user is authenticated
    if (!authService.isAuthenticated()) {
      setError('You must be logged in to play games');
      setLoading(false);
      return;
    }
    if (!gameId || !userId || !providerId) {
      setError('Missing required game information');
      setLoading(false);
      return;
    }
    const enterGameData: EnterGameInput = {
      gameId: String(gameId), // string id for launching game
      userId,
      providerId,
      redirectUrl: window.location.origin + '/game-management',
    };
    const launchGame = async () => {
      try {
        setLoading(true);
        const response = await enterGame(enterGameData);
        setGameData(response);
      } catch (err: any) {
        console.error('Error launching game:', err);
        setError(getErrorMessage(err));
      } finally {
        setLoading(false);
      }
    };
    launchGame();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, gameId, userId, providerId]);

  const handleClose = async () => {
    onClose();

    // Delay exitGame call by 2 seconds
    const sessionToken = gameData?.sessionToken;
    if (sessionToken) {
      setTimeout(async () => {
        try {
          const exitGameInput: ExitGameInput = { sessionToken };
          await exitGame(exitGameInput);
        } catch (error) {
          setTimeout(async () => {
            try {
              console.error('Error exiting game:', error);
              const exitGameInput: ExitGameInput = { sessionToken };
              await exitGame(exitGameInput);
            } catch (error) {
              setTimeout(async () => {
              try {
                console.error('Error exiting game:', error);
                const exitGameInput: ExitGameInput = { sessionToken };
                await exitGame(exitGameInput);
              } catch (error) {
                console.error('Error exiting game:', error);
              }
            },2000)
              console.error('Error exiting game:', error);
            }
          },2000)

        }
      }, 2000);
    }
  };

  // Position update handlers
  const handleEditPosition = () => setEditingPosition(true);
  const handleCancelEditPosition = () => {
    setEditingPosition(false);
    setPositionInput(position ? String(position) : '');
    setPositionSuccess(false);
  };
  const handleSavePosition = async () => {
    if (!id || id === 0) {
      alert('Invalid game ID for position update');
      setPositionUpdating(false);
      return;
    }
    const newPosition = Number(positionInput);
    if (isNaN(newPosition) || newPosition < 1) {
      alert('Please enter a valid position number (minimum 1)');
      return;
    }
    if (newPosition === position) {
      setEditingPosition(false);
      setPositionInput(position ? String(position) : '');
      return;
    }
    setPositionUpdating(true);
    try {
      await updateGamePosition({
        gameId: id, // use numeric id for position update
        environmentId,
        labelId,
        position: newPosition,
      });
      setPositionSuccess(true);
      setTimeout(() => {
        setEditingPosition(false);
        setPositionSuccess(false);
      }, 1200);
    } catch (e) {
      alert('Failed to update position');
    } finally {
      setPositionUpdating(false);
    }
  };

  // Active status toggle handler
  const handleToggleActive = async () => {
    if (!id) return;
    setUpdatingActive(true);
    try {
      if (!localIsActive) {
        // Activate
        await addGameAssignment({
          gameId: id,
          environmentId,
          labelId,
          position: position || 1,
        });
        setLocalIsActive(true);
      } else {
        // Deactivate
        await removeGameAssignment({
          gameId: id,
          environmentId,
          labelId,
        });
        setLocalIsActive(false);
      }
    } catch (e) {
      alert('Failed to update status');
    } finally {
      setUpdatingActive(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={gameName ? `Playing: ${gameName}` : 'Playing Game'}
      maxWidth="800px"
    >
      {loading ? (
        <div className={styles.container}>
          <Loading
            type="spinner"
            size="large"
            text="Launching game..."
            fullScreen={false}
          />
        </div>
      ) : error ? (
        <div className={styles.container}>
          <div className={styles.error}>
            <h2>Error</h2>
            <p>{error}</p>
          </div>
        </div>
      ) : !gameData ? (
        <div className={styles.container}>
          <div className={styles.error}>
            <h2>No Game Data</h2>
            <p>Unable to load game data</p>
          </div>
        </div>
      ) : (
        <div className={styles.container}>
          <div className={styles.header}>
            <div className={styles.gameInfo}>
              <h2>Playing Game</h2>
              <p>Session Token: {gameData.sessionToken}</p>
              <p>
                Balance: {gameData.balance} {gameData.currency}
              </p>
            </div>
            {/* Position and Status controls moved here */}
            <div className={styles.positionStatusContainer}>
              {/* Position */}
              <div className={styles.positionContainer}>
                <span className={styles.positionLabel}>Position:</span>
                {editingPosition ? (
                  <>
                    <input
                      min={1}
                      value={positionInput}
                      onChange={(e) => setPositionInput(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && !positionUpdating)
                          handleSavePosition();
                        else if (e.key === 'Escape') handleCancelEditPosition();
                      }}
                      className={styles.positionInput}
                      disabled={positionUpdating}
                      autoFocus
                    />
                    <button
                      onClick={handleSavePosition}
                      disabled={positionUpdating}
                      className={styles.positionButton}
                    >
                      {positionUpdating
                        ? 'Saving...'
                        : positionSuccess
                          ? 'Saved!'
                          : 'Save'}
                    </button>
                    <button
                      onClick={handleCancelEditPosition}
                      disabled={positionUpdating}
                      className={styles.positionButton}
                    >
                      Cancel
                    </button>
                  </>
                ) : (
                  <>
                    <span className={styles.positionDisplay}>
                      #{positionInput ?? 'N/A'}
                    </span>
                    <button
                      onClick={handleEditPosition}
                      className={styles.positionButton}
                    >
                      Edit
                    </button>
                  </>
                )}
              </div>
              {/* Status */}
              <div className={styles.statusContainer}>
                <div
                  className={
                    styles.statusIndicator +
                    ' ' +
                    (localIsActive === undefined
                      ? styles.statusUnknown
                      : localIsActive
                        ? styles.statusActive
                        : styles.statusInactive)
                  }
                >
                  {localIsActive === undefined
                    ? 'Unknown'
                    : localIsActive
                      ? 'Active'
                      : 'Inactive'}
                </div>
                <button
                  onClick={handleToggleActive}
                  disabled={updatingActive}
                  className={styles.statusButton}
                >
                  {updatingActive
                    ? 'Updating...'
                    : localIsActive
                      ? 'Deactivate'
                      : 'Activate'}
                </button>
              </div>
            </div>
          </div>
          <div className={styles.gameFrame}>
            {gameData.launchUrl?.startsWith('http://') ? (
              <div className={styles.httpFallback}>
                <p>This game requires opening in a new tab due to security restrictions.</p>
                <button
                  className={styles.openGameButton}
                  onClick={() => window.open(gameData.launchUrl, '_blank', 'noopener,noreferrer')}
                >
                  Open Game in New Tab
                </button>
              </div>
            ) : (
              <div className={styles.iframeWrap}>
                <iframe
                  ref={iframeRef}
                  src={gameData.launchUrl}
                  title="Game"
                  className={styles.iframe}
                  allowFullScreen
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  onLoad={onIframeLoad}
                />
                <GameLoadingSplash state={loadState} />
              </div>
            )}
          </div>
        </div>
      )}
    </Modal>
  );
};

// Restore the original EnterGame for route usage
const EnterGame: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const state = location.state as {
    gameId: number;
    userId: number;
    providerId: number;
    gameName?: string;
    position?: number;
    isActive?: boolean;
    environmentId: number;
    labelId: number;
  };
  const [open, setOpen] = useState(true);

  if (!state) {
    // fallback if no state is provided
    return <div>No game info provided</div>;
  }

  return (
    <EnterGameModal
      isOpen={open}
      onClose={() => {
        setOpen(false);
        navigate('/game-management');
      }}
      id={state.gameId}
      gameId={String(state.gameId)}
      userId={state.userId}
      providerId={state.providerId}
      gameName={state.gameName}
      position={state.position}
      isActive={state.isActive}
      environmentId={state.environmentId}
      labelId={state.labelId}
    />
  );
};

export default EnterGame;
